import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@ai-tutor/ui';
import {
  Download,
  ExternalLink,
  Maximize2,
  Minimize2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';

/** Accent buckets for the identity chip, keyed off the design-token colors. */
export type ViewerTone =
  | 'lesson'
  | 'notes'
  | 'reference'
  | 'practice'
  | 'neutral';

const TONE: Record<ViewerTone, string> = {
  // A `/10` tint is nearly the page colour, so the label's real contrast is
  // against the background. lesson (5.20:1) and notes (5.67:1) clear AA at
  // full strength; practice (2.30:1) and reference (2.78:1) do not, so they
  // take the darker `-emphasis` tone - the same split the status colours use.
  lesson: 'bg-lesson/10 text-lesson',
  notes: 'bg-notes/10 text-notes',
  reference: 'bg-reference/10 text-reference-emphasis',
  practice: 'bg-practice/10 text-practice-emphasis',
  neutral: 'bg-muted text-muted-foreground',
};

/** A single icon action rendered in the shell toolbar. */
interface ToolbarButtonProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: ToolbarButtonProps) {
  return (
    <Button
      type='button'
      size='icon'
      variant='ghost'
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      // 32px drawn, 44px tapped on a phone or touch screen. The toolbar's
      // gap widens to 12px there - exactly the two 6px overhangs - so
      // neighbouring tap areas meet without overlapping.
      className='tap-target h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground'
    >
      <Icon className='h-4 w-4' />
    </Button>
  );
}

/** Slim viewer header: identity chip on the left, actions on the right. */
function ViewerToolbar({
  icon: Icon,
  label,
  tone,
  fileUrl,
  externalUrl,
  fullscreen,
  isFullscreen,
  onToggleFullscreen,
  onOpen,
  extra,
}: {
  icon?: LucideIcon;
  label?: string;
  tone: ViewerTone;
  fileUrl?: string;
  externalUrl?: string;
  fullscreen: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onOpen: (url?: string) => void;
  extra?: React.ReactNode;
}) {
  return (
    <div className='flex h-12 flex-shrink-0 items-center gap-2 border-b border-border/60 px-3'>
      {/* Identity chip - gives the toolbar purpose even with no actions */}
      {Icon && label && (
        <div className='flex min-w-0 items-center gap-2'>
          <span
            className={cn(
              'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg',
              TONE[tone]
            )}
          >
            <Icon className='h-4 w-4' />
          </span>
          <span className='truncate text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {label}
          </span>
        </div>
      )}

      <div className='ml-auto flex items-center gap-1 max-sm:gap-3 [@media(pointer:coarse)]:gap-3'>
        {extra && (
          <>
            <div className='flex items-center gap-1 max-sm:gap-3 [@media(pointer:coarse)]:gap-3'>
              {extra}
            </div>
            {(fullscreen || externalUrl || fileUrl) && (
              <span className='mx-1 h-5 w-px bg-border' />
            )}
          </>
        )}
        {fullscreen && (
          <ToolbarButton
            icon={isFullscreen ? Minimize2 : Maximize2}
            label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={onToggleFullscreen}
          />
        )}
        {externalUrl && (
          <ToolbarButton
            icon={ExternalLink}
            label='Open in new tab'
            onClick={() => onOpen(externalUrl)}
          />
        )}
        {fileUrl && (
          <ToolbarButton
            icon={Download}
            label='Download'
            onClick={() => onOpen(fileUrl)}
          />
        )}
      </div>
    </div>
  );
}

interface ViewerShellProps {
  children: React.ReactNode;
  /** Format icon shown in the toolbar identity chip. */
  icon?: LucideIcon;
  /** Short format label (e.g. "PDF", "Markdown") for the identity chip. */
  label?: string;
  /** Accent bucket for the identity chip. */
  tone?: ViewerTone;
  /**
   * Source id whose presigned file URL powers the Download / Open-in-tab
   * actions. Omit for sources with no downloadable file (e.g. pasted
   * text). React Query dedupes this against the viewer's own file-URL
   * query, so this doesn't add a second network request.
   */
  sourceId?: string;
  /** Explicit external URL (link-type sources) - overrides the file URL. */
  externalUrl?: string;
  /** Show the fullscreen toggle (best for pdf / image / video / html). */
  fullscreen?: boolean;
  /** Extra controls rendered on the left of the action group (e.g. zoom). */
  toolbarExtra?: React.ReactNode;
  /** Applied to the scrolling content wrapper. */
  contentClassName?: string;
}

/**
 * Consistent chrome for every source viewer: a slim header with a
 * format-identity chip on the left and download / open-externally /
 * fullscreen (+ per-viewer extras) on the right, over a full-bleed content
 * area. The source name/size already live in the SourcesPanel list above,
 * so the chip surfaces only the *format*, keeping the header lightweight.
 */
export function ViewerShell({
  children,
  icon,
  label,
  tone = 'neutral',
  sourceId,
  externalUrl,
  fullscreen = false,
  toolbarExtra,
  contentClassName,
}: ViewerShellProps) {
  const { data } = useSourceFileUrl(sourceId ?? null);
  // Link sources open externally; file sources download. They're mutually
  // exclusive so we never render two buttons pointing at the same URL.
  const fileUrl = externalUrl ? undefined : data?.url;

  const contentRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () =>
      setIsFullscreen(document.fullscreenElement === contentRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  const open = useCallback((url?: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const showToolbar =
    (!!icon && !!label) ||
    !!toolbarExtra ||
    !!fileUrl ||
    !!externalUrl ||
    fullscreen;

  return (
    // Transparent: the viewer sits on the lesson panel, which already carries
    // the surface. `bg-background` painted the recessed input tone over it.
    <div className='flex h-full flex-col'>
      {showToolbar && (
        <ViewerToolbar
          icon={icon}
          label={label}
          tone={tone}
          fileUrl={fileUrl}
          externalUrl={externalUrl}
          fullscreen={fullscreen}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onOpen={open}
          extra={toolbarExtra}
        />
      )}
      <div
        ref={contentRef}
        // Fullscreen is the one place it needs a fill: a transparent element
        // shown fullscreen displays the browser's black backdrop.
        className={cn(
          'flex-1 overflow-hidden [&:fullscreen]:bg-card',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
}
